import { io, Socket } from 'socket.io-client'
import { ConnectionStatus, SocketConfiguration, EventNames, EventSchema, SocketOptions } from './types'

interface NamespaceConnection {
    socket: Socket
    status: ConnectionStatus
    listeners: Set<(status: ConnectionStatus) => void>
}

/**
 * SocketManager - Manages multiple socket connections for different namespaces
 *
 * Supports connecting to multiple namespaces simultaneously:
 * - Root "/" for OCR/file events
 * - "/notifications" for notification events
 *
 * Each namespace has its own socket instance and connection status.
 */
export class SocketManager {
    private static instance: SocketManager
    private connections: Map<string, NamespaceConnection> = new Map()
    private baseUrl: string = ''
    private socketPath: string = '/socket.io'
    private authOptions: SocketOptions = {}

    private constructor() {}

    public static getInstance(): SocketManager {
        if (!SocketManager.instance) {
            SocketManager.instance = new SocketManager()
        }
        return SocketManager.instance
    }

    /**
     * Initialize base configuration (URL and auth)
     */
    public initialize(config: SocketConfiguration): void {
        const { baseUrl, socketPath } = this.getSocketConnectionConfig(config.url)
        this.baseUrl = baseUrl
        this.socketPath = socketPath
        this.authOptions = config.options || {}
    }

    /**
     * Build socket connection origin and path from API base URL.
     * Example: https://example.com/api => origin=https://example.com, path=/api/socket.io
     */
    private getSocketConnectionConfig(url: string): { baseUrl: string; socketPath: string } {
        const trimmed = (url || '').trim().replace(/\/+$/, '')
        if (!trimmed) {
            return { baseUrl: '', socketPath: '/socket.io' }
        }

        try {
            const parsed = new URL(trimmed)
            const path = parsed.pathname.replace(/\/+$/, '')

            return {
                baseUrl: parsed.origin,
                socketPath: path && path !== '/' ? `${path}/socket.io` : '/socket.io',
            }
        } catch {
            return { baseUrl: trimmed, socketPath: '/socket.io' }
        }
    }

    /**
     * Connect to a specific namespace
     * @param namespace - Namespace to connect (e.g., '/', '/notifications')
     */
    public connect(namespace: string = '/'): Socket {
        const key = namespace || '/'

        // Return existing connection if already connected
        const existing = this.connections.get(key)
        if (existing?.socket?.connected) {
            return existing.socket
        }

        // Build connection URL
        const connectionUrl = key === '/' ? this.baseUrl : `${this.baseUrl}${key}`

        // Create new socket
        const socket = io(connectionUrl, {
            autoConnect: true,
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            path: this.socketPath,
            ...this.authOptions,
        })

        // Create connection record
        const connection: NamespaceConnection = {
            socket,
            status: 'disconnected',
            listeners: new Set(),
        }

        this.connections.set(key, connection)
        this.setupLifecycleListeners(key, socket)

        if (import.meta.env.DEV) {
            console.log(`🔌 Socket connecting to namespace: ${key}`)
        }

        return socket
    }

    /**
     * Disconnect from a specific namespace
     */
    public disconnect(namespace: string = '/'): void {
        const key = namespace || '/'
        const connection = this.connections.get(key)

        if (connection) {
            connection.socket.disconnect()
            connection.listeners.clear()
            this.connections.delete(key)

            if (import.meta.env.DEV) {
                console.log(`🔌 Socket disconnected from namespace: ${key}`)
            }
        }
    }

    /**
     * Disconnect from all namespaces
     */
    public disconnectAll(): void {
        this.connections.forEach((_, namespace) => {
            this.disconnect(namespace)
        })
    }

    /**
     * Update auth options for all connections
     */
    public updateOptions(options: SocketOptions): void {
        this.authOptions = { ...this.authOptions, ...options }

        // Update each connection's auth
        this.connections.forEach(connection => {
            if (connection.socket) {
                connection.socket.io.opts = {
                    ...connection.socket.io.opts,
                    ...options,
                }

                // Reconnect if auth changed
                if (options.auth) {
                    connection.socket.disconnect().connect()
                }
            }
        })
    }

    private setupLifecycleListeners(namespace: string, socket: Socket): void {
        socket.on('connect', () => {
            this.setStatus(namespace, 'connected')
            if (import.meta.env.DEV) {
                console.log(`✅ Socket connected: ${namespace}`)
            }
        })

        socket.on('disconnect', reason => {
            this.setStatus(namespace, 'disconnected')
            if (reason === 'io server disconnect') {
                if (import.meta.env.DEV) {
                    console.warn(`Socket was disconnected by server (${namespace}). Waiting for explicit reconnect.`)
                }
            }
        })

        socket.on('connect_error', error => {
            this.setStatus(namespace, 'error')
            console.error(`Socket connection error (${namespace}):`, error.message)
        })

        socket.on('reconnect_attempt', () => {
            this.setStatus(namespace, 'reconnecting')
        })
    }

    /**
     * Get socket for a specific namespace
     */
    public getSocket(namespace: string = '/'): Socket | null {
        return this.connections.get(namespace || '/')?.socket || null
    }

    /**
     * Get connection status for a specific namespace
     */
    public getStatus(namespace: string = '/'): ConnectionStatus {
        return this.connections.get(namespace || '/')?.status || 'disconnected'
    }

    private setStatus(namespace: string, status: ConnectionStatus): void {
        const connection = this.connections.get(namespace)
        if (connection) {
            connection.status = status
            connection.listeners.forEach(listener => listener(status))
        }
    }

    /**
     * Subscribe to status changes for a specific namespace
     */
    public onStatusChange(namespace: string, callback: (status: ConnectionStatus) => void): () => void {
        const connection = this.connections.get(namespace || '/')
        if (connection) {
            connection.listeners.add(callback)
            return () => {
                connection.listeners.delete(callback)
            }
        }
        return () => {}
    }

    /**
     * Emit event on a specific namespace
     */
    public emit<T extends EventNames>(namespace: string, event: T, payload: EventSchema[T]): void {
        const socket = this.getSocket(namespace)
        socket?.emit(event as string, payload)
    }

    /**
     * Check if connected to a specific namespace
     */
    public isConnected(namespace: string = '/'): boolean {
        return this.getStatus(namespace) === 'connected'
    }

    /**
     * Get all connected namespaces
     */
    public getConnectedNamespaces(): string[] {
        const connected: string[] = []
        this.connections.forEach((connection, namespace) => {
            if (connection.status === 'connected') {
                connected.push(namespace)
            }
        })
        return connected
    }
}
